// src/App.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import FlagTable from "./FlagTable";
import FeatureMatrix from "./components/FeatureMatrix";
import { getOrgs, getFlags, updateFlags, logout as apiLogout } from "./api";
import SignIn from "./SignIn";
import ChangePassword from "./ChangePassword";
import AccessProvisioning from "./pages/accessProvisioning";
import Admin from "./pages/Admin";
import Ingestion from "./pages/Ingestion";
import OrgDetails from "./pages/OrgDetails";
import Settings from "./pages/Settings";
import McpToolIndex from "./pages/McpToolIndex";

const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

export default function App() {
  // --- Auth: track logged-in user (cookie-based session)
  const [user, setUser] = useState(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  // --- Tabs: "org", "feature", "user", "settings", "orgids", "mcptools", "ingestion", "admin"
  const [tab, setTab] = useState("org");

  // --- Org-first state
  const [orgs, setOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);

  const [flags, setFlags] = useState([]);
  const [loadingFlags, setLoadingFlags] = useState(false);

  const [search, setSearch] = useState("");
  const [message, setMessage] = useState(null);

  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [orgError, setOrgError] = useState("");

  // Idle timeout
  const idleTimerRef = useRef(null);

  const clearIdleTimer = () => {
    if (idleTimerRef.current) {
      window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  };

  const restartIdleTimer = () => {
    clearIdleTimer();
    idleTimerRef.current = window.setTimeout(() => {
      // Auto logout on idle
      handleLogout();
    }, IDLE_TIMEOUT_MS);
  };

  const handleLoggedIn = (info) => {
    if (!info) return;
    const normalized = {
      ...info,
      email: info.email || info.username || info.user_email || "",
    };
    setUser(normalized);
    setMustChangePassword(
      !!(normalized.must_change_password || normalized.force_password_change)
    );
  };

  const handleLogout = async () => {
    try {
      await apiLogout();
    } catch {
      // ignore logout errors, we’ll still clear local state
    }
    setUser(null);
    setMustChangePassword(false);
    setOrgs([]);
    setSelectedOrg(null);
    setFlags([]);
    setSearch("");
    setMessage(null);
    setLoadingOrgs(false);
    setOrgError("");
    clearIdleTimer();
  };

  // Idle timer hooks
  useEffect(() => {
    if (!user) {
      clearIdleTimer();
      return;
    }

    restartIdleTimer();
    const events = ["click", "keydown", "mousemove", "scroll", "touchstart"];

    const handler = () => {
      restartIdleTimer();
    };

    events.forEach((evt) => window.addEventListener(evt, handler));

    return () => {
      events.forEach((evt) => window.removeEventListener(evt, handler));
      clearIdleTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Load orgs whenever we have a user/session
  useEffect(() => {
    let ignore = false;

    async function load() {
      setLoadingOrgs(true);
      setOrgError("");
      try {
        const data = await getOrgs();
        if (ignore) return;
        const sorted = (data || [])
          .slice()
          .sort((a, b) => a.name.localeCompare(b.name));
        setOrgs(sorted);
        if (!selectedOrg && sorted.length) {
          setSelectedOrg(sorted[0]);
        }
      } catch (err) {
        if (ignore) return;
        if (err?.response?.status === 401) {
          // Session invalid/expired → force logout
          handleLogout();
          return;
        }
        setOrgError(`Failed to load orgs${err?.message ? ` — ${err.message}` : ""}`);
      } finally {
        if (!ignore) setLoadingOrgs(false);
      }
    }

    if (user && !mustChangePassword) {
      load();
    } else {
      // signed out or blocked for password change → clear org data
      setOrgs([]);
      setSelectedOrg(null);
      setFlags([]);
      setSearch("");
      setMessage(null);
      setLoadingOrgs(false);
      setOrgError("");
    }

    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, mustChangePassword]);

  // Load flags when org changes (only on Org-first tab)
  useEffect(() => {
    let ignore = false;
    async function loadFlags() {
      if (!selectedOrg || tab !== "org" || !user || mustChangePassword) {
        setFlags([]);
        return;
      }
      setLoadingFlags(true);
      try {
        const data = await getFlags(selectedOrg.id, "");
        if (!ignore) setFlags((data || []).sort((a, b) => a.feature_name.localeCompare(b.feature_name)));
      } catch (err) {
        if (!ignore) {
          if (err?.response?.status === 401) {
            handleLogout();
          } else {
            setFlags([]);
          }
        }
      } finally {
        if (!ignore) setLoadingFlags(false);
      }
    }
    if (user && !mustChangePassword) {
      loadFlags();
    }
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg, tab, user, mustChangePassword]);

  // Clear transient messages when context changes
  useEffect(() => {
    setMessage(null);
  }, [tab, selectedOrg]);

  // Handlers
  const handleSelectOrg = (orgObj) => {
    setSelectedOrg(orgObj);
    setSearch("");
    setMessage(null);
  };

  const handleSearchSubmit = async (e) => {
    e.preventDefault();
    if (!selectedOrg) return;
    setMessage(null);
    setLoadingFlags(true);
    try {
      const data = await getFlags(selectedOrg.id, search);
      setFlags((data || []).sort((a, b) => a.feature_name.localeCompare(b.feature_name)));
    } catch (err) {
      if (err?.response?.status === 401) {
        handleLogout();
        return;
      }
      setFlags([]);
      setMessage("Failed to load flags");
    } finally {
      setLoadingFlags(false);
    }
  };

  const handleToggle = async (featureName, enabled) => {
    if (!selectedOrg) return;
    try {
      await updateFlags([selectedOrg.id], [featureName], enabled);
      setMessage(`Feature '${featureName}' was ${enabled ? "enabled" : "disabled"}.`);
      const data = await getFlags(selectedOrg.id, search);
      setFlags((data || []).sort((a, b) => a.feature_name.localeCompare(b.feature_name)));
    } catch (err) {
      if (err?.response?.status === 401) {
        handleLogout();
        return;
      }
      setMessage("Failed to update flag");
    }
  };

  const handleBulkUpdate = async (featureNames, enabled) => {
    if (!selectedOrg) return;
    try {
      await updateFlags([selectedOrg.id], featureNames, enabled);
      setMessage(`${featureNames.length} feature(s) were ${enabled ? "enabled" : "disabled"}.`);
      const data = await getFlags(selectedOrg.id, search);
      setFlags((data || []).sort((a, b) => a.feature_name.localeCompare(b.feature_name)));
    } catch (err) {
      if (err?.response?.status === 401) {
        handleLogout();
        return;
      }
      setMessage("Failed to update flags");
    }
  };

  // Keep the Org-first list in step with Admin > Add / Remove organization
  const handleOrgAdded = (created) => {
    setOrgs((prev) =>
      [...(prev || []), created].sort((a, b) => a.name.localeCompare(b.name))
    );
  };

  const handleOrgRemoved = (orgId) => {
    setOrgs((prev) => (prev || []).filter((o) => o.id !== orgId));
    if (selectedOrg?.id === orgId) setSelectedOrg(null);
  };

  const orgRows = useMemo(
    () => (orgs || []).slice().sort((a, b) => a.name.localeCompare(b.name)),
    [orgs]
  );

  // ---------- GATE THE UI ----------
  if (!user) return <SignIn onLogin={handleLoggedIn} />;

  if (mustChangePassword) {
    return (
      <ChangePassword
        user={user}
        onDone={() => setMustChangePassword(false)}
        onLogout={handleLogout}
      />
    );
  }

  // ---------------- MAIN RETURN ----------------
  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3 className="mb-0">Rohirrim Operations Center</h3>
          {user?.email && (
            <div className="text-muted small">
              Signed in as {user.email} — auto logout after 30 minutes idle
            </div>
          )}
        </div>
        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={handleLogout}
        >
          Sign out
        </button>
      </div>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3" role="tablist">
        <li className="nav-item">
          <button
            id="org-first-tab"
            className={"nav-link " + (tab === "org" ? "active" : "")}
            role="tab"
            aria-selected={tab === "org"}
            aria-controls="org-first-panel"
            onClick={() => setTab("org")}
          >
            Org-first
          </button>
        </li>
        <li className="nav-item">
          <button
            id="feature-first-tab"
            className={"nav-link " + (tab === "feature" ? "active" : "")}
            role="tab"
            aria-selected={tab === "feature"}
            aria-controls="feature-first-panel"
            onClick={() => setTab("feature")}
          >
            Feature-first
          </button>
        </li>
        {/* User Management tab */}
        <li className="nav-item">
          <button
            id="user-mgmt-tab"
            className={"nav-link " + (tab === "user" ? "active" : "")}
            role="tab"
            aria-selected={tab === "user"}
            aria-controls="user-mgmt-panel"
            onClick={() => setTab("user")}
          >
            User Management
          </button>
        </li>

        {/* Settings tab */}
        {(user?.roles?.includes("admin") || user?.roles?.includes("manager")) && (
          <li className="nav-item">
            <button
              id="settings-tab"
              className={"nav-link " + (tab === "settings" ? "active" : "")}
              role="tab"
              aria-selected={tab === "settings"}
              aria-controls="settings-panel"
              onClick={() => setTab("settings")}
            >
              Settings
            </button>
          </li>
        )}

        {/* Org IDs tab — visible to admins and managers */}
        {(user?.roles?.includes("admin") || user?.roles?.includes("manager")) && (
          <li className="nav-item">
            <button
              id="org-ids-tab"
              className={"nav-link " + (tab === "orgids" ? "active" : "")}
              role="tab"
              aria-selected={tab === "orgids"}
              aria-controls="org-ids-panel"
              onClick={() => setTab("orgids")}
            >
              Org IDs
            </button>
          </li>
        )}

        {/* MCP Tool Index tab — visible to admins and managers */}
        {(user?.roles?.includes("admin") || user?.roles?.includes("manager")) && (
          <li className="nav-item">
            <button
              id="mcp-tools-tab"
              className={"nav-link " + (tab === "mcptools" ? "active" : "")}
              role="tab"
              aria-selected={tab === "mcptools"}
              aria-controls="mcp-tools-panel"
              onClick={() => setTab("mcptools")}
            >
              MCP Tools
            </button>
          </li>
        )}

        {/* Ingestion tab — visible to admins */}
        {user?.roles?.includes("admin") && (
          <li className="nav-item">
            <button
              id="ingestion-tab"
              className={"nav-link " + (tab === "ingestion" ? "active" : "")}
              role="tab"
              aria-selected={tab === "ingestion"}
              aria-controls="ingestion-panel"
              onClick={() => setTab("ingestion")}
            >
              Ingestion
            </button>
          </li>
        )}

        {/* Admin tab — same gate as the former Audit Logs tab (auditr role) */}
        {user?.roles?.includes("auditr") && (
          <li className="nav-item">
            <button
              id="admin-tab"
              className={"nav-link " + (tab === "admin" ? "active" : "")}
              role="tab"
              aria-selected={tab === "admin"}
              aria-controls="admin-panel"
              onClick={() => setTab("admin")}
            >
              Admin
            </button>
          </li>
        )}
      </ul>

      {/* Feature-first tab */}
      {tab === "feature" && (
        <div
          id="feature-first-panel"
          role="tabpanel"
          aria-labelledby="feature-first-tab"
        >
          <FeatureMatrix />
        </div>
      )}

      {/* User Management tab */}
      {tab === "user" && (
        <div
          id="user-mgmt-panel"
          role="tabpanel"
          aria-labelledby="user-mgmt-tab"
        >
          <AccessProvisioning />
        </div>
      )}

      {/* Settings tab */}
      {tab === "settings" && (user?.roles?.includes("admin") || user?.roles?.includes("manager")) && (
        <div
          id="settings-panel"
          role="tabpanel"
          aria-labelledby="settings-tab"
        >
          <Settings />
        </div>
      )}

      {/* Org IDs tab */}
      {tab === "orgids" && (user?.roles?.includes("admin") || user?.roles?.includes("manager")) && (
        <div
          id="org-ids-panel"
          role="tabpanel"
          aria-labelledby="org-ids-tab"
        >
          <OrgDetails />
        </div>
      )}

      {/* MCP Tool Index tab */}
      {tab === "mcptools" && (user?.roles?.includes("admin") || user?.roles?.includes("manager")) && (
        <div
          id="mcp-tools-panel"
          role="tabpanel"
          aria-labelledby="mcp-tools-tab"
        >
          <McpToolIndex />
        </div>
      )}

      {/* Ingestion tab */}
      {tab === "ingestion" && user?.roles?.includes("admin") && (
        <div
          id="ingestion-panel"
          role="tabpanel"
          aria-labelledby="ingestion-tab"
        >
          <Ingestion />
        </div>
      )}

      {/* Admin tab — Add organization / Remove organization / Audit trail */}
      {tab === "admin" && user?.roles?.includes("auditr") && (
        <div
          id="admin-panel"
          role="tabpanel"
          aria-labelledby="admin-tab"
        >
          <Admin onOrgAdded={handleOrgAdded} onOrgRemoved={handleOrgRemoved} />
        </div>
      )}

      {/* Org-first two-pane tab */}
      {tab === "org" && (
        <div
          id="org-first-panel"
          className="row"
          role="tabpanel"
          aria-labelledby="org-first-tab"
        >
          {/* Left: organization list */}
          <aside className="col-12 col-md-5 col-lg-4 mb-3">
            <div className="card shadow-sm mb-3">
              <div className="card-header py-2">Organizations</div>
              <div className="card-body p-0">
                {loadingOrgs ? (
                  <div className="p-3 text-muted">Loading organizations…</div>
                ) : orgError ? (
                  <div className="p-3 text-danger">{orgError}</div>
                ) : orgRows.length === 0 ? (
                  <div className="p-3 text-muted">No organizations found.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Org Name</th>
                          <th className="text-end" style={{ width: "120px" }}>
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {orgRows.map((o) => {
                          const selected = selectedOrg?.id === o.id;
                          return (
                            <tr
                              key={o.id}
                              className={selected ? "table-active" : ""}
                              onClick={() => handleSelectOrg(o)}
                              style={{ cursor: "pointer" }}
                            >
                              <td className="text-truncate" title={o.name}>
                                {o.name}
                              </td>
                              <td className="text-end">
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary btn-sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelectOrg(o);
                                  }}
                                >
                                  Select
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </aside>

          {/* Right: feature flags for selected org */}
          <main className="col-12 col-md-7 col-lg-8">
            <div className="card shadow-sm">
              <div className="card-header py-2">
                Feature Flags {selectedOrg ? `— ${selectedOrg.name}` : ""}
              </div>
              <div className="card-body">
                {!selectedOrg ? (
                  <div className="text-muted">
                    Select an organization to view its feature flags.
                  </div>
                ) : (
                  <>
                    <form className="input-group mb-3" onSubmit={handleSearchSubmit}>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search features…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        disabled={loadingFlags}
                      />
                      <button
                        className="btn btn-outline-secondary"
                        type="submit"
                        disabled={loadingFlags}
                      >
                        Search
                      </button>
                    </form>

                    {message && (
                      <div className="alert alert-info py-2" role="alert">
                        {message}
                      </div>
                    )}

                    {loadingFlags ? (
                      <div className="text-muted">Loading flags…</div>
                    ) : (
                      <FlagTable
                        flags={flags}
                        onToggle={handleToggle}
                        onBulkUpdate={handleBulkUpdate}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
          </main>
        </div>
      )}
    </div>
  );
}
